// src/features/analytics/components/AnalyticsCoverageNotice.tsx

import { Alert, Badge, Group, Tooltip } from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';
import { DateTime } from 'luxon';
import { formatRelative, formatTimestamp } from '@/shared/utils/date';
import type { AnalyticsCoverage, AnalyticsFreshness } from '../types';

interface AnalyticsCoverageNoticeProps {
  coverage?: AnalyticsCoverage;
  freshness?: AnalyticsFreshness;
  /** Shown when the range has no coverage at all. */
  emptyMessage?: string;
}

function utcDate(seconds: number): string {
  return DateTime.fromSeconds(seconds, { zone: 'utc' }).toFormat('MMM d, yyyy');
}

/**
 * The shared empty/partial/stale strip (plan §"Empty, partial, stale, and
 * provisional states"). Absence of data is never rendered as a measured zero:
 * callers place this above their content and suppress zero-filled bodies when
 * the empty state is active.
 */
export function AnalyticsCoverageNotice({
  coverage,
  freshness,
  emptyMessage = 'No analytics data for this range.',
}: AnalyticsCoverageNoticeProps) {
  const isEmpty = !coverage || freshness?.state === 'empty';

  if (isEmpty) {
    return (
      <Alert color="gray" icon={<IconInfoCircle size={16} />}>
        {emptyMessage}
      </Alert>
    );
  }

  const isPartial = coverage.partial;
  const isStale = freshness?.state === 'stale';
  if (!isPartial && !isStale) return null;

  return (
    <Group gap="sm">
      {isPartial && (
        <Alert color="yellow" icon={<IconInfoCircle size={16} />} flex={1}>
          Data covers only part of the selected range: {utcDate(coverage.from)} –{' '}
          {utcDate(coverage.to)} (UTC).
        </Alert>
      )}
      {isStale && freshness && (
        <Tooltip label={formatTimestamp(freshness.asOf)}>
          <Badge color="yellow" variant="light">
            Updated {formatRelative(freshness.asOf)}
          </Badge>
        </Tooltip>
      )}
    </Group>
  );
}
